import { creditAmountMutation } from "@/admin/accounts/accountsQuery.ts";
import type { GQAccountItem } from "@/admin/accounts/AccountsTable.tsx";
import { AdminModalButton } from "@/admin/AdminModalButton.tsx";
import { AInput } from "@/admin/form/Fields.tsx";
import { controllerProps, formPropsToAriaProps } from "@/components/forms/ariaController.tsx";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { WTextField } from "@/components/forms/WTextField.tsx";
import { CurrencyBalance } from "@/components/l10n/Currency.tsx";
import { DetailsTable } from "@/components/table/DetailsTable.tsx";
import { DetailsTableRow } from "@/components/table/DetailsTableRow.tsx";
import { siteCurrencyAtom } from "@/config.ts";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { OngoingBalance } from "@/modules/billing/OngoingBalance.tsx";
import { useAtomValue } from "jotai";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";

interface AddRemoveCreditModalProps {
    item: GQAccountItem;
    value: number;
}

interface AddRemoveCreditInputs {
    amount: string;
}

export function AddRemoveCreditButton(props: AddRemoveCreditModalProps) {
    return (
        <AdminModalButton label={<CurrencyBalance value={props.value} />} isDeleted={props.item.is_deleted}>
            <AddRemoveCreditModalContent {...props} />
        </AdminModalButton>
    );
}

export function AddRemoveOngoingButton(props: AddRemoveCreditModalProps) {
    const { value } = props;

    return (
        <AdminModalButton label={<OngoingBalance value={value} />} isDeleted={props.item.is_deleted}>
            <AddRemoveCreditModalContent {...props} />
        </AdminModalButton>
    );
}

function AddRemoveCreditModalContent({ item }: AddRemoveCreditModalProps) {
    //#region Hooks
    const modifyMutation = useStandardMutation(creditAmountMutation);
    const siteCurrency = useAtomValue(siteCurrencyAtom);
    //#endregion

    const { id, running_totals, precalc_credit_amount } = item;

    const onSubmit: SubmitHandler<AddRemoveCreditInputs> = async (inputs) => {
        const { amount } = inputs;

        await modifyMutation.mutateAsync({
            body: {
                billing_account_id: Number(id),
                amount: Number(amount),
                description: "Credit from admin UI",
            },
        });
    };

    const form = useForm<AddRemoveCreditInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            amount: "",
        },
    });

    const { control } = form;

    return (
        <WModalForm form={form} onSubmit={onSubmit} title="Add or remove credit" label="Submit">
            <DetailsTable>
                <DetailsTableRow title="Usage charges">
                    <CurrencyBalance value={running_totals?.subtotal ?? 0} />
                </DetailsTableRow>
                <DetailsTableRow title="Available credit">
                    <CurrencyBalance value={precalc_credit_amount} />
                </DetailsTableRow>
                <DetailsTableRow title="Ongoing balance">
                    <OngoingBalance value={running_totals?.ongoing ?? 0} />
                </DetailsTableRow>
            </DetailsTable>

            <Controller
                control={control}
                name="amount"
                {...controllerProps({ isRequired: true })}
                render={(props) => (
                    <WTextField
                        {...formPropsToAriaProps(props)}
                        headless
                        autoFocus
                        className="max-w-24rem"
                        wide
                        isRequired
                        label={`Credit amount (${siteCurrency})`}
                    >
                        <AInput />
                        <p className="font-size-small color-muted pt-1">
                            Enter the amount of credit you wish to add to the current credit. To remove credit, enter
                            amount with negative value.
                        </p>
                    </WTextField>
                )}
            />
        </WModalForm>
    );
}
